import { useCallback, useEffect, useState, useRef } from 'react';
import {
  ReactFlow,
  Controls,
  ControlButton,
  Background,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  type Node,
  type Edge,
  type NodeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { PersonNode } from './PersonNode';
import { UnionNode } from './UnionNode';
import { api } from '@/lib/api';
import type { TreeNode, TreeEdge } from '@antree/shared';
import { Loader2, Maximize, Minimize, Save } from 'lucide-react';

const nodeTypes = {
  person: PersonNode,
  union: UnionNode,
};

interface FamilyTreeProps {
  projectId: string;
}

interface TreeResponse {
  nodes: TreeNode[];
  edges: TreeEdge[];
}

interface LayoutResult {
  nodes: Node[];
  edges: Edge[];
}

// Layout algorithm with union nodes for couples - supports multiple marriages
function layoutFamilyTree(nodes: TreeNode[], rawEdges: TreeEdge[]): LayoutResult {
  if (nodes.length === 0) return { nodes: [], edges: [] };

  const NODE_WIDTH = 160;
  const NODE_HEIGHT = 100;
  const UNION_SIZE = 12;
  const HORIZONTAL_GAP = 40; // Gap between spouse and union node
  const GENERATION_GAP = 120; // Vertical gap between generations

  // Build relationship maps
  const childrenMap = new Map<string, string[]>(); // parent -> children
  const parentsMap = new Map<string, string[]>(); // child -> parents
  const spouseMap = new Map<string, Set<string>>(); // person -> spouses

  for (const edge of rawEdges) {
    const relationType = edge.data?.relationType;

    if (relationType === 'PARENT_CHILD' && edge.target) {
      if (!childrenMap.has(edge.source)) childrenMap.set(edge.source, []);
      const children = childrenMap.get(edge.source)!;
      if (!children.includes(edge.target)) children.push(edge.target);

      if (!parentsMap.has(edge.target)) parentsMap.set(edge.target, []);
      const parents = parentsMap.get(edge.target)!;
      if (!parents.includes(edge.source)) parents.push(edge.source);
    } else if ((relationType === 'SPOUSE' || relationType === 'PARTNER') && edge.target) {
      if (!spouseMap.has(edge.source)) spouseMap.set(edge.source, new Set());
      if (!spouseMap.has(edge.target)) spouseMap.set(edge.target, new Set());
      spouseMap.get(edge.source)!.add(edge.target);
      spouseMap.get(edge.target)!.add(edge.source);
    }
  }

  // Build unions (one per couple)
  interface Union {
    id: string;
    spouse1: string;
    spouse2: string;
    children: string[];
  }

  const unions: Union[] = [];
  const processedCouples = new Set<string>();

  for (const [personId, spouses] of spouseMap) {
    for (const spouseId of spouses) {
      const coupleKey = [personId, spouseId].sort().join('-');
      if (processedCouples.has(coupleKey)) continue;
      processedCouples.add(coupleKey);

      // Find shared children
      const children1 = childrenMap.get(personId) || [];
      const children2Set = new Set(childrenMap.get(spouseId) || []);
      const sharedChildren = children1.filter(c => children2Set.has(c));

      unions.push({
        id: `union-${coupleKey}`,
        spouse1: personId,
        spouse2: spouseId,
        children: sharedChildren,
      });
    }
  }

  // Map children to their union
  const childToUnion = new Map<string, string>();
  for (const union of unions) {
    for (const childId of union.children) {
      childToUnion.set(childId, union.id);
    }
  }

  // Assign generations (levels) using BFS
  const levels = new Map<string, number>();
  const rootNodes = nodes.filter(n => (parentsMap.get(n.id) || []).length === 0);

  const queue = rootNodes.map(n => ({ id: n.id, level: 0 }));
  while (queue.length > 0) {
    const { id, level } = queue.shift()!;
    if (levels.has(id) && levels.get(id)! >= level) continue;
    levels.set(id, level);
    for (const childId of childrenMap.get(id) || []) {
      queue.push({ id: childId, level: level + 1 });
    }
  }

  // Handle disconnected nodes
  for (const node of nodes) {
    if (!levels.has(node.id)) levels.set(node.id, 0);
  }

  // Ensure spouses are on the same level
  let changed = true;
  while (changed) {
    changed = false;
    for (const [personId, spouses] of spouseMap) {
      const personLevel = levels.get(personId) || 0;
      for (const spouseId of spouses) {
        const spouseLevel = levels.get(spouseId) || 0;
        if (personLevel !== spouseLevel) {
          const maxLevel = Math.max(personLevel, spouseLevel);
          levels.set(personId, maxLevel);
          levels.set(spouseId, maxLevel);
          changed = true;
        }
      }
    }
  }

  // Group nodes by level
  const levelGroups = new Map<number, string[]>();
  for (const [nodeId, level] of levels) {
    if (!levelGroups.has(level)) levelGroups.set(level, []);
    levelGroups.get(level)!.push(nodeId);
  }

  const sortedLevels = Array.from(levelGroups.keys()).sort((a, b) => a - b);
  const positions = new Map<string, { x: number; y: number }>();

  // Position each level
  for (const level of sortedLevels) {
    const nodesInLevel = levelGroups.get(level)!;
    const y = level * (NODE_HEIGHT + GENERATION_GAP);

    // Group connected spouses together
    const processedInLevel = new Set<string>();
    const groups: string[][] = [];

    for (const nodeId of nodesInLevel) {
      if (processedInLevel.has(nodeId)) continue;

      const group: string[] = [nodeId];
      const toProcess = [nodeId];
      processedInLevel.add(nodeId);

      while (toProcess.length > 0) {
        const currentId = toProcess.pop()!;
        for (const spouseId of spouseMap.get(currentId) || []) {
          if (nodesInLevel.includes(spouseId) && !processedInLevel.has(spouseId)) {
            group.push(spouseId);
            processedInLevel.add(spouseId);
            toProcess.push(spouseId);
          }
        }
      }
      groups.push(group);
    }

    // Position groups - account for union nodes between spouses
    let currentX = 0;
    for (const group of groups) {
      // Find hub person (most spouses)
      let hubPerson = group[0];
      let maxSpouses = 0;
      for (const pid of group) {
        const count = spouseMap.get(pid)?.size || 0;
        if (count > maxSpouses) {
          maxSpouses = count;
          hubPerson = pid;
        }
      }

      // Order: spouses around hub
      const hubSpouses = [...(spouseMap.get(hubPerson) || [])].filter(s => group.includes(s));
      const leftSpouses = hubSpouses.slice(0, Math.ceil(hubSpouses.length / 2));
      const rightSpouses = hubSpouses.slice(Math.ceil(hubSpouses.length / 2));

      // Position: leftSpouses - [unions] - hub - [unions] - rightSpouses
      // Each pair has a union node between them

      // Left spouses
      for (const spouseId of leftSpouses) {
        positions.set(spouseId, { x: currentX, y });
        currentX += NODE_WIDTH + HORIZONTAL_GAP;
        // Space for union node
        currentX += UNION_SIZE + HORIZONTAL_GAP;
      }

      // Hub person
      positions.set(hubPerson, { x: currentX, y });
      currentX += NODE_WIDTH + HORIZONTAL_GAP;

      // Right spouses
      for (const spouseId of rightSpouses) {
        // Space for union node
        currentX += UNION_SIZE + HORIZONTAL_GAP;
        positions.set(spouseId, { x: currentX, y });
        currentX += NODE_WIDTH + HORIZONTAL_GAP;
      }

      currentX += HORIZONTAL_GAP; // Gap between groups
    }

    // Center the level
    const allX = nodesInLevel.map(id => positions.get(id)?.x || 0);
    const minX = Math.min(...allX);
    const maxX = Math.max(...allX);
    const centerShift = -(minX + maxX) / 2;
    for (const nodeId of nodesInLevel) {
      const pos = positions.get(nodeId);
      if (pos) positions.set(nodeId, { x: pos.x + centerShift, y: pos.y });
    }
  }

  // Position union nodes BETWEEN spouses (same Y level, horizontally centered)
  for (const union of unions) {
    const pos1 = positions.get(union.spouse1);
    const pos2 = positions.get(union.spouse2);
    if (pos1 && pos2) {
      positions.set(union.id, {
        x: (pos1.x + pos2.x) / 2,
        y: (pos1.y + pos2.y) / 2 + NODE_HEIGHT / 2 - UNION_SIZE / 2, // Same vertical center as persons
      });
    }
  }

  // Center children under their parent union
  for (const level of [...sortedLevels].reverse().slice(0, -1)) {
    const childLevel = level + 1;
    const childrenInLevel = levelGroups.get(childLevel) || [];

    // Group children by their union
    const childGroups = new Map<string, string[]>();
    for (const childId of childrenInLevel) {
      const unionId = childToUnion.get(childId);
      const key = unionId || `orphan`;
      if (!childGroups.has(key)) childGroups.set(key, []);
      childGroups.get(key)!.push(childId);
    }

    for (const [unionId, children] of childGroups) {
      if (!unionId.startsWith('union-')) continue;

      const unionPos = positions.get(unionId);
      if (!unionPos) continue;

      const childPositions = children.map(c => positions.get(c)).filter(Boolean) as { x: number; y: number }[];
      if (childPositions.length === 0) continue;

      const childMinX = Math.min(...childPositions.map(p => p.x));
      const childMaxX = Math.max(...childPositions.map(p => p.x));
      const childCenterX = (childMinX + childMaxX) / 2;
      const shift = unionPos.x - childCenterX;

      for (const childId of children) {
        const pos = positions.get(childId);
        if (pos) positions.set(childId, { x: pos.x + shift, y: pos.y });
      }
    }
  }

  // Build result nodes
  const resultNodes: Node[] = nodes.map(node => ({
    id: node.id,
    type: 'person',
    position: positions.get(node.id) || { x: 0, y: 0 },
    data: node.data as unknown as Record<string, unknown>,
  }));

  for (const union of unions) {
    const pos = positions.get(union.id);
    if (pos) {
      resultNodes.push({
        id: union.id,
        type: 'union',
        position: pos,
        data: {},
      });
    }
  }

  // Build edges with correct handles
  const resultEdges: Edge[] = [];

  for (const union of unions) {
    const pos1 = positions.get(union.spouse1);
    const pos2 = positions.get(union.spouse2);
    const unionPos = positions.get(union.id);

    if (pos1 && pos2 && unionPos) {
      // Determine which spouse is on which side
      const spouse1IsLeft = pos1.x < pos2.x;
      const leftSpouse = spouse1IsLeft ? union.spouse1 : union.spouse2;
      const rightSpouse = spouse1IsLeft ? union.spouse2 : union.spouse1;

      // Left spouse → Union (uses right handle to exit, enters union's left/spouse1)
      resultEdges.push({
        id: `${union.id}-left`,
        source: leftSpouse,
        sourceHandle: 'right',
        target: union.id,
        targetHandle: 'spouse1',
        type: 'straight',
        style: { stroke: '#a06a35', strokeWidth: 2 },
      });

      // Right spouse → Union (uses left handle to exit, enters union's right/spouse2)
      resultEdges.push({
        id: `${union.id}-right`,
        source: rightSpouse,
        sourceHandle: 'left',
        target: union.id,
        targetHandle: 'spouse2',
        type: 'straight',
        style: { stroke: '#a06a35', strokeWidth: 2 },
      });
    }

    // Union → Children (vertical lines)
    for (const childId of union.children) {
      resultEdges.push({
        id: `${union.id}-child-${childId}`,
        source: union.id,
        sourceHandle: 'children',
        target: childId,
        type: 'smoothstep',
        style: { stroke: '#a06a35', strokeWidth: 2 },
      });
    }
  }

  // Single-parent children: connect directly to parent (no union node)
  const childrenInUnions = new Set<string>();
  for (const union of unions) {
    for (const childId of union.children) {
      childrenInUnions.add(childId);
    }
  }

  for (const [parentId, children] of childrenMap) {
    for (const childId of children) {
      if (!childrenInUnions.has(childId)) {
        // This child only has this one parent - direct connection
        resultEdges.push({
          id: `single-parent-${parentId}-${childId}`,
          source: parentId,
          sourceHandle: 'bottom',
          target: childId,
          type: 'smoothstep',
          style: { stroke: '#a06a35', strokeWidth: 2 },
        });
      }
    }
  }

  return { nodes: resultNodes, edges: resultEdges };
}

interface SavedLayout {
  positions: Record<string, { x: number; y: number }>;
}

interface LayoutResponse {
  layout: SavedLayout | null;
}

export function FamilyTree({ projectId }: FamilyTreeProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const initialLayoutRef = useRef<LayoutResult | null>(null);

  const loadTree = useCallback(async () => {
    setLoading(true);

    // Load tree data and saved layout in parallel
    const [treeResponse, layoutResponse] = await Promise.all([
      api.get<TreeResponse>(`/projects/${projectId}/persons/tree`),
      api.get<LayoutResponse>(`/projects/${projectId}/tree-layout`),
    ]);

    if (treeResponse.success && treeResponse.data) {
      const { nodes: rawNodes, edges: rawEdges } = treeResponse.data;
      const { nodes: layoutedNodes, edges: layoutedEdges } = layoutFamilyTree(rawNodes, rawEdges);

      // Add projectId to each node's data and apply saved positions
      const savedPositions = layoutResponse.success && layoutResponse.data?.layout?.positions
        ? layoutResponse.data.layout.positions
        : {};

      const finalNodes = layoutedNodes.map(node => ({
        ...node,
        position: savedPositions[node.id] || node.position,
        data: { ...node.data, projectId },
      }));

      initialLayoutRef.current = { nodes: finalNodes, edges: layoutedEdges };
      setNodes(finalNodes);
      setEdges(layoutedEdges);
    }
    setLoading(false);
  }, [projectId, setNodes, setEdges]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  // Track fullscreen changes (native API for desktop)
  useEffect(() => {
    const handleFullscreenChange = () => {
      // Only update if we're using native fullscreen
      if (document.fullscreenElement === null && isFullscreen) {
        // Native fullscreen was exited (e.g., via Escape key)
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [isFullscreen]);

  // Prevent body scroll when in fullscreen mode (for mobile)
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  // Handle node position changes
  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    onNodesChange(changes);

    // Check if any position changed
    const hasPositionChange = changes.some(
      change => change.type === 'position' && change.dragging === false
    );
    if (hasPositionChange) {
      setHasUnsavedChanges(true);
    }
  }, [onNodesChange]);

  // Toggle fullscreen (CSS-based for mobile compatibility)
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Save positions
  const savePositions = useCallback(async () => {
    setSaving(true);

    const positions: Record<string, { x: number; y: number }> = {};
    for (const node of nodes) {
      positions[node.id] = node.position;
    }

    const response = await api.put(`/projects/${projectId}/tree-layout`, {
      positions,
    });

    if (response.success) {
      setHasUnsavedChanges(false);
    }
    setSaving(false);
  }, [nodes, projectId]);

  if (loading) {
    return (
      <div className="h-[500px] flex items-center justify-center bg-muted/20 rounded-lg">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="h-[500px] flex items-center justify-center bg-muted/20 rounded-lg">
        <p className="text-muted-foreground">Keine Personen vorhanden</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`overflow-hidden bg-white ${
        isFullscreen
          ? 'fixed inset-0 z-50 h-screen w-screen'
          : 'border rounded-lg h-[500px]'
      }`}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={true}
      >
        <Controls>
          {/* Save button */}
          <ControlButton
            onClick={savePositions}
            title={hasUnsavedChanges ? "Layout speichern" : "Keine Änderungen"}
            disabled={!hasUnsavedChanges || saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className={`h-4 w-4 ${hasUnsavedChanges ? 'text-bronze-500' : ''}`} />
            )}
          </ControlButton>

          {/* Fullscreen button */}
          <ControlButton onClick={toggleFullscreen} title={isFullscreen ? "Vollbild beenden" : "Vollbild"}>
            {isFullscreen ? (
              <Minimize className="h-4 w-4" />
            ) : (
              <Maximize className="h-4 w-4" />
            )}
          </ControlButton>
        </Controls>
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
      </ReactFlow>
    </div>
  );
}
